/* eslint no-console: 0 */
/* global axios */
import ApiClient from '../ApiClient';

const camelToSnakeCase = str =>
  str.replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`);

export const buildCreatePayload = ({
  message,
  isPrivate,
  contentAttributes,
  echoId,
  files,
  ccEmails = '',
  bccEmails = '',
  toEmails = '',
  templateParams,
  scheduledAt,
}) => {
  let payload;
  if (files && files.length !== 0) {
    payload = new FormData();
    if (message) {
      payload.append('content', message);
    }
    files.forEach(file => {
      payload.append('attachments[]', file);
    });
    payload.append('private', isPrivate);
    payload.append('echo_id', echoId);
    payload.append('cc_emails', ccEmails);
    payload.append('bcc_emails', bccEmails);

    if (toEmails) {
      payload.append('to_emails', toEmails);
    }
    if (contentAttributes) {
      payload.append('content_attributes', JSON.stringify(contentAttributes));
    }

    if (scheduledAt) {
      payload.append('scheduled_at', scheduledAt);
    }
    if (templateParams) {
      payload.append('template_params', JSON.stringify(templateParams));
    }
  } else {
    payload = {
      content: message,
      private: isPrivate,
      echo_id: echoId,
      content_attributes: contentAttributes,
      cc_emails: ccEmails,
      bcc_emails: bccEmails,
      to_emails: toEmails,
      template_params: templateParams,
      scheduled_at: scheduledAt,
    };
  }
  return payload;
};

export const buildUpdatePayload = payloadObject => {
  const formData = new FormData();

  Object.entries(payloadObject).forEach(([key, value]) => {
    if (value === undefined || value === null) return;

    if (key === 'replacedAttachments') {
      value.forEach((item, index) => {
        formData.append(`replaced_attachments[${index}][id]`, item.id);
        formData.append(`replaced_attachments[${index}][file]`, item.file);
      });
      return;
    }

    const snakeKey = camelToSnakeCase(key);

    if (
      typeof value === 'object' &&
      !(value instanceof File) &&
      !(value instanceof Blob)
    ) {
      formData.append(snakeKey, JSON.stringify(value));
    } else {
      formData.append(snakeKey, value);
    }
  });

  return formData;
};

class MessageApi extends ApiClient {
  constructor() {
    super('conversations', { accountScoped: true });
  }

  create({
    conversationId,
    message,
    private: isPrivate,
    contentAttributes,
    echo_id: echoId,
    files,
    ccEmails = '',
    bccEmails = '',
    toEmails = '',
    templateParams,
    scheduledAt,
  }) {
    return axios({
      method: 'post',
      url: `${this.url}/${conversationId}/messages`,
      data: buildCreatePayload({
        message,
        isPrivate,
        contentAttributes,
        echoId,
        files,
        ccEmails,
        bccEmails,
        toEmails,
        templateParams,
        scheduledAt,
      }),
    });
  }

  updateContent(conversationId, messageId, payloadObject) {
    return axios.patch(
      `${this.url}/${conversationId}/messages/${messageId}`,
      buildUpdatePayload(payloadObject)
    );
  }

  updateSchedule(conversationId, messageId, scheduledAt) {
    return axios.patch(
      `${this.url}/${conversationId}/messages/${messageId}/schedule`,
      { scheduled_at: scheduledAt }
    );
  }

  retry_update(conversationId, messageId) {
    return axios.post(
      `${this.url}/${conversationId}/messages/${messageId}/retry_update`
    );
  }

  retry_destroy(conversationId, messageId) {
    return axios.post(
      `${this.url}/${conversationId}/messages/${messageId}/retry_destroy`
    );
  }

  delete(conversationID, messageId) {
    return axios.delete(`${this.url}/${conversationID}/messages/${messageId}`);
  }

  retry(conversationID, messageId) {
    return axios.post(
      `${this.url}/${conversationID}/messages/${messageId}/retry`
    );
  }

  getPreviousMessages({ conversationId, after, before }) {
    const params = { before };
    if (after && Number(after) !== Number(before)) {
      params.after = after;
    }
    return axios.get(`${this.url}/${conversationId}/messages`, { params });
  }

  getScheduledMessages(conversationId) {
    return axios.get(`${this.url}/${conversationId}/messages`, {
      params: { status: 'scheduled' },
    });
  }

  getMessageEdits(conversationId, messageId) {
    return axios.get(
      `${this.url}/${conversationId}/messages/${messageId}/edits`
    );
  }

  translateMessage(conversationId, messageId, targetLanguage) {
    return axios.post(
      `${this.url}/${conversationId}/messages/${messageId}/translate`,
      {
        target_language: targetLanguage,
      }
    );
  }
}

export default new MessageApi();
